// src/app/pages/patient/patient-list/patient-profile/patient-profile.component.ts

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule, NgForOf, NgIf } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import Swal from 'sweetalert2';

import { environment } from 'environments/environment';

import { DefaultResponse } from '@core/models/Http/DefaultResponse';
import { PatientDetails } from '@core/models/PatientDetails';
import { UserRole } from '@core/models/Enums/UserRole';

import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';

import { MatIcon } from '@angular/material/icon';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { MatList, MatListItem } from '@angular/material/list';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';

import { FormatMobilePipe } from '@core/pipes/format-mobile.pipe';
import { CreateEditService } from '@core/http/create-edit-service';
import { UserService } from '@core/http/table-data-services/User.service';
import { HttpMesageHandlerService } from '@core/service/http-mesage-handler';

import { PrescriptionByIdPatientComponent } from '../../../prescription/prescription-list/prescription-by-id-patient/prescription-by-id-patient.component';

import {
  TutorChangeEmailDialogComponent,
  TutorChangeEmailDialogResult
} from '@shared/components/tutor-change-email-dialog/tutor-change-email-dialog.component';
import {
  TutorCreateDialogComponent,
  TutorCreateDialogResult
} from '@shared/components/tutor-create-dialog/tutor-create-dialog.component';
import {
  TutorSelectDialogComponent,
  TutorSelectItem,
  TutorSelectDialogResult
} from '@shared/components/tutor-select-dialog/tutor-select-dialog.component';

import { TutorService, UpdateTutorPacienteRequest } from '@core/service/tutor.service';

// ✅ PIN dialog (standalone)
import { PatientPinDialogComponent } from '@shared/components/patient-pin-dialog/patient-pin-dialog.component';
import {MedicamentoTypeLabelPipe} from "@core/pipes/medicamento-type-label.pipe";
import {RegionWordPipe} from "@core/pipes/region-word.pipe";

@Component({
  selector: 'app-patient-profile',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    MatIcon,
    MatTab,
    MatTabGroup,
    MatTabLabel,
    ReactiveFormsModule,
    FormatMobilePipe,
    MatList,
    MatListItem,
    CommonModule,
    NgForOf,
    PrescriptionByIdPatientComponent,
    NgIf,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatAccordion,
    MatButtonModule,
    MatTooltipModule,
    MedicamentoTypeLabelPipe,
    RegionWordPipe
  ],
  templateUrl: './patient-profile.component.html',
  styleUrl: './patient-profile.component.scss'
})
export class PatientProfileComponent implements OnInit {
  param: string | null = null; // <-- idUsuario
  userImage: string | null = null;

  tutorImage: string | null = null;
  tutoradoImages: Record<string, string> = {};

  userRole = '';
  tutor: any = null;

  tutorados: any[] = [];
  isTutor = false;

  patient: PatientDetails = {} as PatientDetails;

  private readonly imageCache = new Map<string, string>();

  // =========================================================
  // ✅ MEDICAMENTOS ACTIVOS (perfil)
  // =========================================================
  medicamentosActivosPaciente: any[] = [];
  hasHiddenMeds = false;
  showHiddenMeds = false;
  hiddenMedsPin: string | null = null;
  isLoadingMedsActivos = false;

  trackMedActivo = (_: number, m: any) =>
    m?.idDetalleReceta ?? m?.idReceta ?? m?.drug ?? _;

  constructor(
    private http: HttpClient,
    private ceService: CreateEditService,
    private tutorService: TutorService,
    private userService: UserService,
    private route: ActivatedRoute,
    private httpMsgHandler: HttpMesageHandlerService,
    private dialog: MatDialog
  ) {
    this.route.paramMap.subscribe(params => {
      const p = params.get('param');
      this.param = p ? atob(p) : null;
    });
  }

  ngOnInit(): void {
    if (this.param) {
      this.getPatientDetails(this.param);
      this.getPacientesByTutor(this.param);
    }
  }

  // =========================================================
  // ✅ CARGA PERFIL
  // =========================================================
  getPatientDetails(param: string) {
    this.ceService.getpatientByIdUser(param).subscribe({
      next: (res: DefaultResponse<PatientDetails | PatientDetails[]>) => {
        const data = Array.isArray(res.data)
          ? (res.data[0] as PatientDetails)
          : (res.data as PatientDetails);

        if (!data) return;

        this.patient = data;

        this.userImage = (data as any).imagen
          ? `data:image/png;base64,${(data as any).imagen}`
          : null;

        const roleValue =
          (data as any).TipoUsuario ??
          (data as any).tipoUsuario ??
          (data as any).idTipoUsuario ??
          '';

        this.userRole = this.getRoleName(roleValue);

        const idPaciente = String((data as any)?.idPaciente ?? '').trim();

        if (idPaciente) {
          this.getTutorByPaciente(idPaciente);

          // ✅ cargar medicamentos activos (sin PIN al inicio)
          this.loadMedicamentosActivosPaciente(idPaciente, null);
        } else {
          this.tutor = null;
          this.tutorImage = null;

          // reset meds
          this.loadMedicamentosActivosPaciente(undefined, null);
        }
      },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err)
    });
  }

  // =========================================================
  // ✅ MEDICAMENTOS ACTIVOS - ENDPOINT
  // GET /api/DetalleRecetas/GetSoloMedicamentosActivos?idPaciente=...&pin=...
  // =========================================================
  public loadMedicamentosActivosPaciente(idPaciente?: string, pin?: string | null): void {
    if (!idPaciente) {
      this.medicamentosActivosPaciente = [];
      this.hasHiddenMeds = false;
      this.showHiddenMeds = false;
      this.hiddenMedsPin = null;
      return;
    }

    const url = `${environment.backend_url}DetalleRecetas/GetSoloMedicamentosActivos`;
    this.isLoadingMedsActivos = true;

    const params: any = { idPaciente };
    if (pin && pin.trim().length > 0) params.pin = pin.trim();

    this.http.get<any>(url, { params }).subscribe({
      next: (res) => {
        const raw = typeof res === 'string' ? JSON.parse(res) : res;
        const data = raw?.data ?? raw ?? {};

        const hasHidden = Boolean(data?.hasHidden ?? data?.HasHidden ?? false);
        const meds = data?.medicamentos ?? data?.Medicamentos ?? [];

        this.hasHiddenMeds = hasHidden;

        // ✅ si traemos pin, significa que estamos mostrando ocultos
        this.showHiddenMeds = !!(pin && pin.trim().length > 0);
        this.hiddenMedsPin = this.showHiddenMeds ? (pin ?? null) : null;

        this.medicamentosActivosPaciente = Array.isArray(meds) ? meds : [];
        this.isLoadingMedsActivos = false;
      },
      error: (err) => {
        this.medicamentosActivosPaciente = [];
        this.showHiddenMeds = false;
        this.hiddenMedsPin = null;
        this.isLoadingMedsActivos = false;
        this.httpMsgHandler.handleMsg(err);
      }
    });
  }

  // ✅ Botón "MOSTRAR/OCULTAR MEDICAMENTOS OCULTOS"
  async toggleMedicamentosOcultos(): Promise<void> {
    const idPaciente = String((this.patient as any)?.idPaciente ?? '').trim();
    if (!idPaciente) return;

    // Si ya estaban visibles por pin => ocultar = recargar sin pin
    if (this.showHiddenMeds) {
      this.showHiddenMeds = false;
      this.hiddenMedsPin = null;
      this.loadMedicamentosActivosPaciente(idPaciente, null);
      return;
    }

    // Pedir PIN y recargar con pin
    const mode =
      this.userRole === 'Paciente' ? 'PACIENTE'
        : this.userRole === 'Tutor' ? 'TUTOR'
          : this.userRole === 'Admin' ? 'ADMIN'
            : 'MEDICO';

    const dialogRef = this.dialog.open(PatientPinDialogComponent, {
      disableClose: false,
      autoFocus: false,
      width: '560px',
      maxWidth: '92vw',
      panelClass: ['rmd-pin-dialog-panel'],
      data: {
        mode,
        title: 'MEDICAMENTOS PROTEGIDOS',
        message: 'EL PACIENTE PROTEGIÓ MEDICAMENTOS CON UN PIN. INGRESA EL PIN PARA VISUALIZARLOS.',
        optional: true,
      }
    });

    const pin = await firstValueFrom(dialogRef.afterClosed());

    // cerró el diálogo
    if (pin === undefined as any) return;

    // continuar sin pin
    if (!pin) {
      this.showHiddenMeds = false;
      this.hiddenMedsPin = null;
      this.loadMedicamentosActivosPaciente(idPaciente, null);
      return;
    }

    // con pin
    this.showHiddenMeds = true;
    this.hiddenMedsPin = pin;
    this.loadMedicamentosActivosPaciente(idPaciente, pin);
  }

  // =========================================================
  // ✅ TUTOR (si el paciente tiene)
  // GET /api/Tutores/GetTutorByIdPaciente?idPaciente=...
  // =========================================================
  private getTutorByPaciente(idPaciente: string): void {
    this.tutorService.getTutorByPaciente(idPaciente).subscribe({
      next: (res: any) => {
        const payload = res?.data ?? res ?? null;
        const tutorObj = Array.isArray(payload) ? (payload[0] ?? null) : payload;
        this.tutor = tutorObj && Object.keys(tutorObj).length ? tutorObj : null;

        const idTutorUsuario =
          this.tutor?.idTutorUsuario ??
          this.tutor?.IdTutorUsuario ??
          this.tutor?.idTutor ??
          this.tutor?.IdTutor ??
          this.tutor?.idUsuario ??
          this.tutor?.IdUsuario ??
          null;

        if (idTutorUsuario) this.loadUserImage(String(idTutorUsuario), 'TUTOR');
        else this.tutorImage = null;
      },
      error: (_: HttpErrorResponse) => {
        this.tutor = null;
        this.tutorImage = null;
      }
    });
  }

  // =========================================================
  // ✅ TUTORADOS (si el usuario es tutor)
  // GET /api/Tutores/GetPacientesByTutor?idTutor=...
  // =========================================================
  private getPacientesByTutor(idUsuarioTutor: string): void {
    this.tutorService.getPacientesByTutor(idUsuarioTutor).subscribe({
      next: (res: any) => {
        const payload = res?.data ?? res ?? [];
        const list = Array.isArray(payload) ? payload : [];

        this.tutorados = list;
        this.isTutor = list.length > 0;

        this.tutorados.forEach(row => {
          const idUsuario = row?.idUsuario ?? row?.IdUsuario ?? null;
          if (idUsuario) this.loadUserImage(String(idUsuario), 'TUTORADO');
        });
      },
      error: (_: HttpErrorResponse) => {
        this.tutorados = [];
        this.isTutor = false;
      }
    });
  }

  // =========================================================
  // ✅ ELIMINAR TUTOR
  // DELETE /api/Tutores/EliminarTutor?idPaciente=...
  // =========================================================
  eliminarTutor(): void {
    const idPaciente = (this.patient as any)?.idPaciente
      ? String((this.patient as any).idPaciente)
      : '';

    if (!idPaciente) {
      Swal.fire({
        icon: 'warning',
        title: 'SIN PACIENTE',
        text: 'NO SE PUDO OBTENER EL ID DEL PACIENTE.'
      });
      return;
    }

    Swal.fire({
      icon: 'warning',
      title: 'ELIMINAR TUTOR',
      text: '¿ESTAS SEGURO QUE DESEAS ELIMINAR EL TUTOR DE ESTE PACIENTE?',
      showCancelButton: true,
      confirmButtonText: 'SI, ELIMINAR',
      cancelButtonText: 'CANCELAR'
    }).then(result => {
      if (!result.isConfirmed) return;

      Swal.fire({
        title: 'ELIMINANDO TUTOR...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading()
      });

      this.tutorService.eliminarTutor(idPaciente).subscribe({
        next: () => {
          Swal.close();

          this.tutor = null;
          this.tutorImage = null;

          Swal.fire({
            icon: 'success',
            title: 'LISTO',
            text: 'EL TUTOR FUE ELIMINADO.'
          });
        },
        error: (err: HttpErrorResponse) => {
          Swal.close();
          this.httpMsgHandler.handleMsg(err);
        }
      });
    });
  }

  // =========================================================
  // ✅ CAMBIAR TUTOR
  // 1) pedir email (modal)
  // 2) buscar GetTutorByEmail
  //    - si no existe -> abrir modal crear tutor
  // 3) PUT UpdateTutorPaciente {idPaciente, idNuevoTutor}
  // =========================================================
  cambiarTutor(): void {
    const idPaciente = (this.patient as any)?.idPaciente
      ? String((this.patient as any).idPaciente)
      : '';

    if (!idPaciente) {
      Swal.fire({
        icon: 'warning',
        title: 'SIN PACIENTE',
        text: 'NO SE PUDO OBTENER EL ID DEL PACIENTE.'
      });
      return;
    }

    const ref = this.dialog.open(TutorChangeEmailDialogComponent, {
      width: '620px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      data: { email: '' }
    });

    ref.afterClosed().subscribe((result: TutorChangeEmailDialogResult | null) => {
      if (!result?.email) return;

      const email = result.email.trim().toLowerCase();

      Swal.fire({
        title: 'BUSCANDO TUTOR...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading()
      });

      this.tutorService.getTutorByEmail(email).subscribe({
        next: (res: any) => {
          Swal.close();

          const payload = res?.data ?? res ?? [];
          const listRaw = Array.isArray(payload) ? payload : [];

          const options = this.mapTutorEmailResults(listRaw, email);

          if (options.length === 0) {
            this.abrirCrearTutorYConfirmarActualizar(idPaciente, email);
            return;
          }

          if (options.length === 1) {
            this.confirmarYActualizarTutor(idPaciente, options[0]);
            return;
          }

          const refSel = this.dialog.open(TutorSelectDialogComponent, {
            width: '720px',
            maxWidth: '95vw',
            disableClose: true,
            autoFocus: false,
            data: { email, options }
          });

          refSel.afterClosed().subscribe((sel: TutorSelectDialogResult | null) => {
            if (!sel?.selected?.idUsuario) return;
            this.confirmarYActualizarTutor(idPaciente, sel.selected);
          });
        },
        error: (err: HttpErrorResponse) => {
          Swal.close();
          this.httpMsgHandler.handleMsg(err);
        }
      });
    });
  }

  private abrirCrearTutorYActualizarRelacion(idPaciente: string, email: string): void {
    const ref = this.dialog.open(TutorCreateDialogComponent, {
      width: '980px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      data: { email }
    });

    ref.afterClosed().subscribe((created: TutorCreateDialogResult | null) => {
      if (!created?.idUsuario) return;

      this.actualizarTutorPaciente(idPaciente, created.idUsuario);
    });
  }

  private actualizarTutorPaciente(idPaciente: string, idNuevoTutor: string): void {
    const payload: UpdateTutorPacienteRequest = {
      idPaciente,
      idNuevoTutor
    };

    this.tutorService.updateTutorPaciente(payload).subscribe({
      next: (_: any) => {
        this.getTutorByPaciente(idPaciente);

        Swal.fire({ icon: 'success', title: 'LISTO', text: 'EL TUTOR FUE ACTUALIZADO.' }).then();
      },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err)
    });
  }

  // =========================================================
  // ✅ IMAGENES
  // =========================================================
  private loadUserImage(idUsuario: string, target: 'TUTOR' | 'TUTORADO'): void {
    if (!idUsuario) return;

    const cached = this.imageCache.get(idUsuario);
    if (cached !== undefined) {
      if (target === 'TUTOR') this.tutorImage = cached || null;
      else this.tutoradoImages[idUsuario] = cached;
      return;
    }

    this.userService.getUserImageBase64(idUsuario).subscribe({
      next: (img: string) => {
        const normalized = (img ?? '').trim();
        const value = normalized ? normalized : '';
        this.imageCache.set(idUsuario, value);

        if (target === 'TUTOR') this.tutorImage = value || null;
        else this.tutoradoImages[idUsuario] = value;
      },
      error: _ => {
        this.imageCache.set(idUsuario, '');
        if (target === 'TUTOR') this.tutorImage = null;
      }
    });
  }

  // =========================================================
  // ✅ HELPERS
  // =========================================================
  getRoleName(roleValue: string): string {
    const entry = Object.entries(UserRole).find(([, value]) => value === roleValue);
    return entry ? entry[0] : '';
  }

  getTutoradoNombre(row: any): string {
    const n = row?.nombres ?? row?.nombre ?? row?.Nombre ?? '';
    const a1 = row?.primerApellido ?? row?.apellidoPaterno ?? row?.PrimerApellido ?? '';
    const a2 = row?.segundoApellido ?? row?.apellidoMaterno ?? row?.SegundoApellido ?? '';
    return `${n} ${a1} ${a2}`.replace(/\s+/g, ' ').trim();
  }

  getTutoradoIdUsuario(row: any): string {
    return String(row?.idUsuario ?? row?.IdUsuario ?? '');
  }

  getTutoradoImageSrc(row: any): string {
    const idUsuario = this.getTutoradoIdUsuario(row);
    const img = idUsuario ? (this.tutoradoImages[idUsuario] ?? '') : '';
    return img || 'assets/images/paciente.png';
  }

  private mapTutorEmailResults(listRaw: any[], emailFallback: string): TutorSelectItem[] {
    const clean = (v: any) => String(v ?? '').replace(/\s+/g, ' ').trim();

    const out: TutorSelectItem[] = [];

    for (const x of (listRaw ?? [])) {
      const idUsuario = clean(
        x?.tutor_idUsuario ??
        x?.Tutor_IdUsuario ??
        x?.tutor_IdUsuario ??
        x?.idUsuario ??
        x?.IdUsuario ??
        ''
      );

      if (!idUsuario) continue;

      const nombres = clean(x?.tutor_Nombres ?? x?.Tutor_Nombres ?? x?.nombres ?? x?.Nombres ?? '');
      const ap1 = clean(x?.tutor_PrimerApellido ?? x?.Tutor_PrimerApellido ?? x?.primerApellido ?? x?.PrimerApellido ?? '');
      const ap2 = clean(x?.tutor_SegundoApellido ?? x?.Tutor_SegundoApellido ?? x?.segundoApellido ?? x?.SegundoApellido ?? '');

      const nombreCompleto = clean(`${nombres} ${ap1} ${ap2}`) || 'TUTOR';

      const email = clean(
        x?.tutor_Email ??
        x?.Tutor_Email ??
        x?.email ??
        x?.Email ??
        emailFallback
      );

      if (out.some(o => o.idUsuario === idUsuario)) continue;

      out.push({ idUsuario, nombreCompleto, email });
    }

    out.sort((a, b) => a.nombreCompleto.localeCompare(b.nombreCompleto));

    return out;
  }

  private abrirCrearTutorYConfirmarActualizar(idPaciente: string, email: string): void {
    const ref = this.dialog.open(TutorCreateDialogComponent, {
      width: '980px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      data: { email }
    });

    ref.afterClosed().subscribe((created: TutorCreateDialogResult | null) => {
      if (!created?.idUsuario) return;

      const chosen: TutorSelectItem = {
        idUsuario: created.idUsuario,
        nombreCompleto: created.nombreCompleto || 'TUTOR',
        email: created.email ?? email
      };

      this.confirmarYActualizarTutor(idPaciente, chosen);
    });
  }

  private confirmarYActualizarTutor(idPaciente: string, tutor: TutorSelectItem): void {
    const nombre = (tutor?.nombreCompleto ?? 'ESTE TUTOR').toUpperCase();

    Swal.fire({
      icon: 'question',
      title: 'CONFIRMAR CAMBIO DE TUTOR',
      html: `¿ESTAS SEGURO QUE DESEAS ASIGNAR A <b>${nombre}</b> COMO TUTOR DEL PACIENTE?`,
      showCancelButton: true,
      confirmButtonText: 'SI, ASIGNAR',
      cancelButtonText: 'CANCELAR'
    }).then(r => {
      if (!r.isConfirmed) return;

      Swal.fire({
        title: 'ACTUALIZANDO TUTOR...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading()
      });

      const payload: UpdateTutorPacienteRequest = {
        idPaciente,
        idNuevoTutor: tutor.idUsuario
      };

      this.tutorService.updateTutorPaciente(payload).subscribe({
        next: () => {
          Swal.close();
          this.getTutorByPaciente(idPaciente);

          Swal.fire({
            icon: 'success',
            title: 'LISTO',
            text: 'EL TUTOR FUE ACTUALIZADO.'
          });
        },
        error: (err: HttpErrorResponse) => {
          Swal.close();
          this.httpMsgHandler.handleMsg(err);
        }
      });
    });
  }

  formatFechaCorta(iso: string | undefined | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    // MX: dd/mm/yyyy hh:mm
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yy} ${hh}:${mi}`;
  }
}
